import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import 'mantine-datatable/styles.css';

import { Badge, createTheme, type MantineColorsTuple, MantineProvider } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { App } from './App';
import { brand } from './theme/brand';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

/** Deco Stop teal ramp built around the site's bar-sea / bar-aqua / bar-deep tokens. */
const decostop: MantineColorsTuple = [
  '#f1f7f7',
  '#ddeded',
  '#bcdada',
  '#95c5c5',
  '#6aaead',
  '#3c9594',
  brand.sea, // shade 6 — #0e7c7b, 5.01:1 white-on
  '#0d5f5d',
  '#0c4948',
  brand.deep, // shade 9 — #0b3b39
];

const theme = createTheme({
  primaryColor: 'decostop',
  primaryShade: { light: 6, dark: 5 },
  colors: { decostop },
  // Body stays on the system stack (dense tables read better than a condensed face);
  // Barlow Condensed — the site's brand face — carries the headings.
  fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  respectReducedMotion: true,
  headings: { fontFamily: brand.font, fontWeight: '700' },
  components: {
    /**
     * A badge shows its whole word.
     *
     * Mantine clips a badge's label and puts an ellipsis on it. That is wrong twice over on the
     * lists this portal is made of: the word disappears — Bills to pay read "UNP…" and "PART…" in
     * the one column that says whether money has gone out, Filed months read "TAKEN BACK OF…" —
     * and, worse, a clipped label stops counting towards how wide its column wants to be, so the
     * column collapses to nothing and *causes* the clipping it then suffers from. Every badge in
     * this portal holds a status word or a short sentence somebody needs to read; none of them is
     * long enough to be worth hiding. Measured across all nineteen screens at both a wide and a
     * narrow window: nothing is clipped afterwards and no page overflows sideways.
     */
    Badge: Badge.extend({ styles: { label: { overflow: 'visible' } } }),
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <MantineProvider defaultColorScheme="auto" theme={theme}>
      <Notifications position="top-right" />
      {/*
        The query cache has to be OUTSIDE the modal manager, not inside it.

        Modal content is rendered from the modal manager's own place in the tree, not from the page
        that opened it — so with these two the other way round, anything inside a modal was outside
        the cache's provider. That is not a missed refresh; it throws. "Set register PIN" took the whole
        portal to a blank screen the moment it was clicked, on the one screen that is supposed to be
        the single truthful place about a person, and it did it silently: the app is rendered without
        an error boundary, so all the owner sees is black.

        Only the PIN form has ever put live content in a modal, which is why this survived — the
        other three are confirm dialogs with a paragraph of text in them. Fixed here rather than in
        the PIN form, because the next modal that needs to read anything would hit exactly this.
      */}
      <QueryClientProvider client={queryClient}>
        <ModalsProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </ModalsProvider>
      </QueryClientProvider>
    </MantineProvider>
  </StrictMode>,
);
